import * as React from 'react';
import { DatasetField, Theme } from '../../../shared/api/api';
import { addModifiers, formatMonthYear, normalizeCapitalization } from '../../../shared/helpers';
import { getDataFieldPath, getSingleDatasetPath } from '../dataExplorerRouterPaths';
import { renderCoverageCell, renderDateCoverageCell } from '../DataTableCommon/DataTableCommon';
import { DataTableColumn } from '../DataTableCommon/DataTableCommonTypes';
import { DataExplorerLink } from '../dataExplorerHistoryContext';
import { CopyToClipboard } from '../CopyToClipboard/CopyToClipboard';
import { CellContext, Row } from '@tanstack/react-table';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import { ThemeLabel } from '../DataTableCommon/ThemeLabel';
import { getUniqueValues } from '../RegionTables/RegionTableColumns';

interface DataFieldLabelsParams {
  themes?: Theme[];
  isNew?: boolean;
}

export function DataFieldLabels({ themes, isNew }: DataFieldLabelsParams) {
  const isTheme = (themes?.length ?? 0) > 0;
  if (!isTheme && !isNew) {
    return <></>;
  }

  return (
    <div className="data-table__label-container">
      {isNew && <div className={addModifiers('data-table__label', ['new'])}>new</div>}
      {isTheme && <ThemeLabel themes={themes as Theme[]} />}
    </div>
  );
}

export const DATASET_NAME_TABLE_COLUMN = 'dataset.name';

export function getReactTableColumns(
  trackEvent: (event: string) => void,
  isPyramidMultiplierVisible: boolean
): DataTableColumn<DatasetField>[] {
  return [
    {
      id: DATASET_NAME_TABLE_COLUMN,
      name: 'dataExplorer.datasetFields.name',
      accessor: (field: DatasetField) => field.dataset.name,
      accessorFn: (field: DatasetField) => field.dataset.name,
      active: true,
      display: true,
      type: 'choices',
      enableSorting: false,
      size: 100,
      cell: rowData => {
        const row = rowData.row;
        return (
          <div
            onClickCapture={() => {
              trackEvent('de_click_dataset');
            }}
            className={addModifiers('data-table__cell-content', ['with-wrapping'])}
          >
            <DataExplorerLink className={addModifiers('link', ['wrap'])} to={getSingleDatasetPath({ datasetId: row.original.dataset.id })}>
              {row.getValue(DATASET_NAME_TABLE_COLUMN)}
            </DataExplorerLink>
          </div>
        );
      },
    },
    {
      id: 'id',
      name: 'dataExplorer.datasetFields.id',
      accessor: (field: DatasetField) => field.id,
      accessorFn: (field: DatasetField) => field.id,
      active: true,
      display: true,
      type: 'choices',
      size: 80,
      cell: rowData => {
        const row = rowData.row;
        const originalField = row.original;
        const { dataset, themes, new: isNew } = originalField;
        const isAggregated = rowData.cell.getIsAggregated();
        if (isAggregated) {
          return (
            <div>
              <span className={'link'}>
                <FormattedMessageHelper id="dataExplorer.allFields.seeAllFields" />
              </span>
            </div>
          );
        }

        return (
          <div>
            <div
              onClickCapture={() => {
                trackEvent('de_click_field');
              }}
              className={addModifiers('data-table__cell-content', ['with-wrapping', 'break-word'])}
            >
              <DataExplorerLink
                className={addModifiers('link', ['wrap'])}
                to={getDataFieldPath({ datasetId: dataset?.id, fieldId: getRowValue(row, 'id') })}
              >
                {getRowValue(row, 'id')}
              </DataExplorerLink>
              <CopyToClipboard value={getRowValue(row, 'id')} />

              <DataFieldLabels isNew={isNew} themes={themes} />
            </div>
          </div>
        );
      },
    },
    {
      id: 'description',
      name: 'dataExplorer.datasetFields.description',
      accessor: (field: DatasetField) => field.description,
      accessorFn: (field: DatasetField) => field.description,
      active: true,
      enableSorting: false,
      display: true,
      type: 'choices',
      size: 120,
      cell: rowData => {
        const row = rowData.row;
        return (
          <div className={addModifiers('data-table__cell-content', ['with-wrapping', 'clamp'])}>{getRowValue(row, 'description')}</div>
        );
      },
    },
    {
      id: 'type',
      name: 'dataExplorer.datasetFields.type',
      accessor: (field: DatasetField) => field.type,
      accessorFn: (field: DatasetField) => field.type,
      active: true,
      display: true,
      type: 'choices',
      size: 40,
      cell: rowData => {
        const row = rowData.row;
        return (
          <div className={addModifiers('data-table__cell-content', ['with-wrapping'])}>
            {normalizeCapitalization(getRowValue(row, 'type'))}
          </div>
        );
      },
    },
    ...(isPyramidMultiplierVisible
      ? [
          {
            id: 'pyramidMultiplier',
            name: 'dataExplorer.datasetFields.pyramidMultiplier',
            accessorFn: (datasetField: DatasetField) => datasetField.pyramidMultiplier,
            active: true,
            display: true,
            enableSorting: false,
            type: 'decimal',
            size: 45,
            cell: (rowData: CellContext<DatasetField, DatasetField>) => {
              const row = rowData.row;
              return (
                <div className={addModifiers('data-table__cell-content', ['with-wrapping'])}>
                  {getUniqueValues(row, 'pyramidMultiplier') as React.ReactNode}
                </div>
              );
            },
          },
        ]
      : []),
    {
      id: 'coverage',
      name: 'dataExplorer.datasetFields.coverage',
      accessor: (field: DatasetField) => field.coverage,
      accessorFn: (field: DatasetField) => field.coverage,
      active: true,
      display: true,
      type: 'percent',
      size: 40,
      cell: renderCoverageCell,
    },
    {
      id: 'dateCoverage',
      name: 'dataExplorer.datasetFields.dateCoverage',
      accessor: (field: DatasetField) => field.dateCoverage,
      accessorFn: (field: DatasetField) => field.dateCoverage,
      active: true,
      display: true,
      type: 'percent',
      size: 40,
      cell: renderDateCoverageCell,
    },
    {
      id: 'alphaCount',
      name: 'dataExplorer.datasetFields.alphaCount',
      accessor: (field: DatasetField) => field.alphaCount,
      accessorFn: (field: DatasetField) => field.alphaCount,
      active: true,
      display: true,
      type: 'integer',
      size: 50,
      cell: rowData => {
        const row = rowData.row;
        return <div className={addModifiers('data-table__cell-content', ['with-wrapping'])}>{getRowValue(row, 'alphaCount')}</div>;
      },
    },
    {
      id: 'dateCreated',
      name: 'dataExplorer.datasetFields.dateAdded',
      accessor: (field: DatasetField) => field.dateCreated,
      accessorFn: (field: DatasetField) => field.dateCreated,
      active: true,
      display: true,
      type: 'choices',
      size: 60,
      cell: rowData => {
        const row = rowData.row;
        return (
          <div className={addModifiers('data-table__cell-content', ['with-wrapping'])}>
            {formatMonthYear(getRowValue(row, 'dateCreated'))}
          </div>
        );
      },
    },
  ];
}

function getRowValue(row: Row<DatasetField>, key: string) {
  if (typeof row.getValue === 'function') {
    return row.getValue(key);
  }
  // @ts-expect-error dynamic column key not in react-table row type
  return row[key];
}
